import React, { useState, useEffect } from 'react';
import { 
  X, 
  Plus, 
  Edit, 
  Trash2, 
  Share2, 
  Users, 
  Lock, 
  Check,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import dashboardPageService from '../../../services/dashboardPageService';

const PageManagementModal = ({ 
  isOpen, 
  onClose, 
  pages = [], 
  currentPage, 
  onPageCreated, 
  onPageUpdated, 
  onPageDeleted,
  user 
}) => {
  const [mode, setMode] = useState('list'); // 'list', 'create', 'edit'
  const [formData, setFormData] = useState({
    layoutName: '',
    description: '',
    isShared: false
  });
  const [editingPageId, setEditingPageId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sharingLoading, setSharingLoading] = useState({});

  const canSharePages = dashboardPageService.hasManagerOrAdminRole(user);
  const { personal, shared } = dashboardPageService.categorizPages(
    pages, 
    user?.tenantUserId || user?.userId
  );

  useEffect(() => {
    if (!isOpen) {
      setMode('list');
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      layoutName: '',
      description: '',
      isShared: false
    });
    setEditingPageId(null);
  };

  const handleCreate = () => {
    setMode('create');
    resetForm();
  };

  const handleEdit = (page) => {
    setMode('edit');
    setEditingPageId(page.LayoutID);
    setFormData({
      layoutName: page.LayoutName,
      description: page.Description || '',
      isShared: page.IsShared || false
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.layoutName.trim()) {
      toast.error('Page name is required');
      return;
    }

    setLoading(true);

    try {
      if (mode === 'create') {
        const result = await dashboardPageService.createPage({
          layoutName: formData.layoutName,
          description: formData.description,
          isShared: formData.isShared,
          tabsConfig: [{ id: 'main', label: 'Main', order: 0 }]
        });

        toast.success(formData.isShared ? 'Shared page created successfully' : 'Page created successfully');
        onPageCreated && onPageCreated(result.data);
        setMode('list');
        resetForm();
      } else if (mode === 'edit') {
        await dashboardPageService.updatePage(editingPageId, {
          layoutName: formData.layoutName,
          description: formData.description,
          tabsConfig: pages.find(p => p.LayoutID === editingPageId)?.tabsConfig || []
        });

        toast.success('Page updated successfully');
        onPageUpdated && onPageUpdated(editingPageId);
        setMode('list');
        resetForm();
      }
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error(error.response?.data?.error || 'Failed to save page');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page? All widgets will be removed.')) {
      return;
    }

    try {
      await dashboardPageService.deletePage(pageId);
      toast.success('Page deleted successfully');
      onPageDeleted && onPageDeleted(pageId);
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error(error.response?.data?.error || 'Failed to delete page');
    }
  };

  const handleToggleShare = async (pageId, currentIsShared) => {
    setSharingLoading(prev => ({ ...prev, [pageId]: true }));

    try {
      await dashboardPageService.sharePage(pageId, !currentIsShared);
      toast.success(!currentIsShared ? 'Page shared successfully' : 'Page unshared successfully');
      onPageUpdated && onPageUpdated(pageId);
    } catch (error) {
      console.error('Error toggling share status:', error);
      toast.error(error.response?.data?.error || 'Failed to update sharing status');
    } finally {
      setSharingLoading(prev => ({ ...prev, [pageId]: false }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-cyan-600 to-cyan-500">
          <h2 className="text-xl font-bold text-white">
            {mode === 'list' && 'Manage Dashboard Pages'}
            {mode === 'create' && 'Create New Page'}
            {mode === 'edit' && 'Edit Page'}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {mode === 'list' ? (
            <div className="space-y-6">
              {/* Create Button */}
              <button
                onClick={handleCreate}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-cyan-600 to-cyan-500 text-white rounded-lg hover:from-cyan-700 hover:to-cyan-600 transition-all"
              >
                <Plus className="h-5 w-5" />
                <span>Create New Page</span>
              </button>

              {/* Personal Pages */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                  <Lock className="h-5 w-5 text-gray-600" />
                  <span>My Pages</span>
                  <span className="text-sm text-gray-500">({personal.length})</span>
                </h3>
                
                {personal.length === 0 ? (
                  <p className="text-gray-500 text-sm italic">No personal pages yet</p>
                ) : (
                  <div className="space-y-2">
                    {personal.map(page => (
                      <div
                        key={page.LayoutID}
                        className={`p-4 border rounded-lg flex items-center justify-between ${
                          currentPage?.LayoutID === page.LayoutID
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-semibold text-gray-900">{page.LayoutName}</h4>
                            {page.IsDefault && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                                Default
                              </span>
                            )}
                            {currentPage?.LayoutID === page.LayoutID && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                Current
                              </span>
                            )}
                          </div>
                          {page.Description && (
                            <p className="text-sm text-gray-600 mt-1">{page.Description}</p>
                          )}
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          {canSharePages && (
                            <button
                              onClick={() => handleToggleShare(page.LayoutID, page.IsShared)}
                              disabled={sharingLoading[page.LayoutID]}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Share with all users"
                            >
                              {sharingLoading[page.LayoutID] ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                              ) : (
                                <Share2 className="h-5 w-5" />
                              )}
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleEdit(page)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          
                          <button
                            onClick={() => handleDelete(page.LayoutID)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Shared Pages */}
              {shared.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center space-x-2">
                    <Users className="h-5 w-5 text-green-600" />
                    <span>Shared Pages</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">({shared.length})</span>
                  </h3>
                  
                  <div className="space-y-2">
                    {shared.map(page => (
                      <div
                        key={page.LayoutID}
                        className={`p-4 border rounded-lg flex items-center justify-between ${
                          currentPage?.LayoutID === page.LayoutID
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-400'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100">{page.LayoutName}</h4>
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-xs font-medium rounded">
                              Shared
                            </span>
                            {currentPage?.LayoutID === page.LayoutID && (
                              <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs font-medium rounded">
                                Current
                              </span>
                            )}
                          </div>
                          {page.Description && (
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{page.Description}</p>
                          )}
                          {page.sharedByName && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Shared by {page.sharedByName}
                            </p>
                          )}
                        </div>

                        {page.canEdit && (
                          <div className="flex items-center space-x-2 ml-4">
                            {canSharePages && (
                              <button
                                onClick={() => handleToggleShare(page.LayoutID, page.IsShared)}
                                disabled={sharingLoading[page.LayoutID]}
                                className="p-2 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors disabled:opacity-50"
                                title="Unshare"
                              >
                                {sharingLoading[page.LayoutID] ? (
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600 dark:border-orange-400"></div>
                                ) : (
                                  <Share2 className="h-5 w-5" />
                                )}
                              </button>
                            )}
                            
                            <button
                              onClick={() => handleEdit(page)}
                              className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit className="h-5 w-5" />
                            </button>
                            
                            <button
                              onClick={() => handleDelete(page.LayoutID)}
                              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Create/Edit Form
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Page Name *
                </label>
                <input
                  type="text"
                  value={formData.layoutName}
                  onChange={(e) => setFormData({ ...formData, layoutName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Sales Dashboard, Marketing Overview"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of this page"
                  rows={3}
                />
              </div>

              {mode === 'create' && canSharePages && (
                <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="isShared"
                    checked={formData.isShared}
                    onChange={(e) => setFormData({ ...formData, isShared: e.target.checked })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="isShared" className="text-sm font-medium text-gray-900 cursor-pointer">
                      Share with all users
                    </label>
                    <p className="text-xs text-gray-600 mt-1">
                      When enabled, all users in your tenant will be able to see and use this page
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Check className="h-5 w-5" />
                      <span>{mode === 'create' ? 'Create Page' : 'Save Changes'}</span>
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => setMode('list')}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageManagementModal;

